<script setup lang="ts">
import {
  FileManager,
  FileManagerNotice,
  Spinner,
} from '@/components/document/sub-components/file-manager';
import { usePaneHeight } from '@/composeables/use-pane-height';
import {
  sanitizePathSegment,
  useProjectMounts,
} from '@/composeables/use-project-mounts';
import {
  PhFolderDashed,
  PhLockSimple,
  PhWarningCircle,
} from '@phosphor-icons/vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

/**
 * Every provisioned project in one file manager. The component itself needs no change for this — it
 * takes a mount table and knows nothing about projects (D4), so "many projects at the root" is purely
 * a matter of the prefixes this host builds: two segments instead of one, which makes the root list a
 * namespace row per project and each project's two libraries one level down.
 *
 * `site.title` is the ledger's stored `provisionedTitle`, which `deriveSiteTitle` already formats as
 * `"{number} - {name}"` — it IS the display label, so no project-name query is needed here. It can lag
 * a Fortnox rename; accepted, and a deep link into the old title fails gracefully via
 * useNavigationPathGuard. `sanitizePathSegment` is not optional: the title is data, and a slash in it
 * would silently invent a namespace level (D1).
 *
 * Unprobed (D5). Probing every project up front would be 2N eager Graph calls — throttling, and with
 * `retry: false` a throttled batch would leave the whole table permanently empty. Safe because a
 * library this user cannot read errors on *entry* into the existing FolderLoadError instead of being
 * hidden, and a mount with no `rootId` already falls back to `getItemReference` for a drop onto its
 * root. Probing per project on expansion is the natural follow-up, and is a host-side change only.
 *
 * Known limitation, deliberately not fixed here: AdminPage wraps its `router-view` in `<keep-alive>`,
 * while the pane measures its height on mount and on window resize only — so switching admin tabs away
 * and back leaves `paneHeight` stale until the next resize. The fix is `onActivated(updatePaneHeight)`
 * inside usePaneHeight.
 */
const { mounts, status } = useProjectMounts({
  prefixOf: (site, spec) =>
    `/${sanitizePathSegment(site.title)}/${spec.title}`,
});

// Sized to the exact box <FileManager> would have occupied, so a notice sits where the pane would be.
const { paneRef, paneHeight } = usePaneHeight();
</script>

<template>
  <FileManager v-if="status === 'available'" :mounts="mounts" />
  <div v-else ref="paneRef" :style="{ height: `${paneHeight}px` }">
    <FileManagerNotice v-if="status === 'loading'">
      <template #badge><Spinner /></template>
    </FileManagerNotice>
    <FileManagerNotice
      v-else-if="status === 'unprovisioned'"
      :icon="PhFolderDashed"
      :message="t('documentPane.noProjectsProvisionedMessage')"
    />
    <FileManagerNotice
      v-else-if="status === 'no-access'"
      :icon="PhLockSimple"
      :message="t('documentPane.noAccessMessage')"
    />
    <FileManagerNotice
      v-else
      :icon="PhWarningCircle"
      :message="t('documentPane.loadErrorMessage')"
    />
  </div>
</template>
